import React from 'react';
import { Modal, Button, Select, Spin, Divider, Typography, message } from 'antd';
import { AssistInputState } from '../services/assist-input';
import { openUrl } from '../utils/linkify';

const { Text } = Typography;
const { Option } = Select;

export interface AssistTextOption {
  enqNo: number;
  enqName: string;
}

interface AssistInputModalProps {
  open: boolean;
  onClose: () => void;
  options: AssistTextOption[];
  selectedValue: number | null;
  onValueChange: (value: number) => void;
  contentHtml: string;
  loading: boolean;
  error: string;
  errorRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
  /** オプション一覧の取得自体が失敗したとき true */
  optionsError?: boolean;
  /** hidden input の id（ページごとに異なる） */
  hiddenInputId: string;
  // Modal 外観カスタマイズ
  width?: number;
  wrapClassName?: string;
  bodyStyle?: React.CSSProperties;
  /**
   * 'standard': AIP/FRP レイアウト（インラインローディング、エラーは Divider 下）
   * 'flex':     BCD レイアウト（全体ローディング、エラーは先頭、flex-column 配置）
   */
  layout?: 'standard' | 'flex';
}

const MODAL_Z_OVERLAY_MASK = 1030;
const MODAL_Z_OVERLAY_WRAP = 1031;

const WRAP_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  // dvh / dvw でモバイルブラウザの URL バー伸縮にも追従させる（古いブラウザは vh/vw にフォールバック）
  width: '100dvw',
  height: '100dvh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: MODAL_Z_OVERLAY_WRAP,
};

// CSS 解析ユーティリティ（reset / initializeAssistForm 共通）
function parseCssData(): any[] {
  const enqCss = (window as any).enqCss;
  if (typeof enqCss === 'string' && enqCss.trim() !== '') {
    try {
      const rules = enqCss.match(/([^{]+)\s*\{([^}]+)\}/g) || [];
      return rules.map((rule: string) => {
        const m = rule.match(/([^{]+)\s*\{([^}]+)\}/) || [];
        return [m[1]?.trim(), m[2]?.trim()];
      });
    } catch {
      return [];
    }
  }
  if (Array.isArray(enqCss)) return enqCss as any[];
  return [];
}

const AssistInputModal: React.FC<AssistInputModalProps> = ({
  open,
  onClose,
  options,
  selectedValue,
  onValueChange,
  contentHtml,
  loading,
  error,
  errorRef,
  contentRef,
  optionsError,
  hiddenInputId,
  width = 600,
  wrapClassName = 'selectEnqueteInputAid',
  bodyStyle = { overflow: 'hidden', padding: '2px' },
  layout = 'standard',
}) => {
  // ----- リセットボタンハンドラ -----
  const handleReset = () => {
    try {
      const aptoenqform = document.getElementById('aptoenqform');
      if (!aptoenqform) {
        message.error('フォームエレメントの取得に失敗しました。再度選択してください。');
        return;
      }
      const enqQuestions = (window as any).enqQuestions;
      if (!enqQuestions || !Array.isArray(enqQuestions) || enqQuestions.length === 0) {
        message.error('質問リストが取得できません。再度選択してください。');
        return;
      }
      aptoenqform.innerHTML = '';
      const state = new AssistInputState(enqQuestions, parseCssData());
      state.reset();
    } catch (e) {
      message.error('リセット中にエラーが発生しました: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  // ----- 選択ボタンハンドラ -----
  const handleSelect = () => {
    if (loading || !contentHtml) return;
    try {
      const aptoenqform = document.getElementById('aptoenqform');
      if (!aptoenqform) {
        message.error('フォームエレメントの取得に失敗しました。再度選択してください。');
        return;
      }
      if (aptoenqform.children.length === 0) {
        message.info('質問が表示されていません。再度選択してください。');
        return;
      }
      const modal = (window as any).SelectEnqueteInputAidModal;
      if (modal?.checkInput()) {
        modal.confirmEnquete();
      }
    } catch (e) {
      message.error('エラーが発生しました: ' + (e instanceof Error ? e.message : String(e)));
    }
  };

  // ----- パーツ -----
  const errorBlock = error ? (
    <div
      ref={errorRef}
      style={{
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '16px',
        color: '#a8071a',
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>⚠️ エラー</div>
      <div style={{ whiteSpace: 'pre-line' }}>{error}</div>
    </div>
  ) : null;

  const selectSection = (
    <div
      style={{
        marginBottom: layout === 'flex' ? undefined : 16,
        ...(layout === 'flex' ? { flexShrink: 0 } : {}),
      }}
    >
      <Select
        placeholder="アシスト入力を選択"
        style={{ width: '100%' }}
        onChange={onValueChange}
        value={selectedValue || undefined}
        id="selectEnqueteInputAid_menu"
        getPopupContainer={(trigger) => trigger.parentElement || document.body}
        dropdownStyle={{ overscrollBehavior: 'contain' } as React.CSSProperties}
      >
        {options.map((option) => (
          <Option key={option.enqNo} value={option.enqNo}>
            {option.enqName}
          </Option>
        ))}
      </Select>
      <input type="hidden" id={hiddenInputId} value="" />
    </div>
  );

  const contentSection = (
    <div
      style={{
        marginBottom: 16,
        minHeight: layout === 'flex' ? 200 : 100,
        ...(layout === 'flex' ? { flex: 1 } : {}),
      }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
          <Spin />
        </div>
      ) : (
        <>
          {!selectedValue ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '30px 0' }}>
              アシスト入力を選択してください
            </div>
          ) : (
            <div id="aptoenqform-container" style={{ minHeight: '150px' }}>
              <div id="aptoenqform" className="fontM" data-testid="aptoenqform">
                {/* 質問が動的に挿入される */}
              </div>
            </div>
          )}
          <div
            style={{ display: 'none' }}
            dangerouslySetInnerHTML={{ __html: contentHtml }}
            ref={contentRef}
            className="assist-content-hidden"
            id="aptoenq_content_hidden"
            data-testid="aptoenq-content-hidden"
          />
          <div id="selectEnqueteInputAid_template_necessary" style={{ display: 'none' }}>
            <div className="aptoenq_necessary aptoenq_red d-none">
              ※<span className="aptoenq_red">赤字</span>は必須項目です。
            </div>
          </div>
          <div id="selectEnqueteInputAid_template_textArea" style={{ display: 'none' }}>
            <textarea className="select-follow-enquete-textarea" />
          </div>
        </>
      )}
    </div>
  );

  const buttonsSection = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        ...(layout === 'flex' ? { flexShrink: 0, marginTop: 'auto', paddingTop: '16px' } : {}),
      }}
    >
      <Button
        id="selectEnqueteInputAid_btnReset"
        onClick={handleReset}
        disabled={!contentHtml || loading}
      >
        リセット
      </Button>
      <Button
        id="selectEnqueteInputAid_btnSelect"
        type="primary"
        onClick={handleSelect}
        disabled={!contentHtml || loading}
      >
        選択
      </Button>
    </div>
  );

  // layout='flex': BCD スタイル（ローディング時は全体置換、エラーは先頭）
  // layout='standard': AIP/FRP スタイル（ローディングはコンテンツ内、エラーは Divider 下）
  const body =
    layout === 'flex' ? (
      loading ? (
        <div style={{ textAlign: 'center', padding: '24px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>アシストテキストを読み込み中...</div>
        </div>
      ) : options.length === 0 ? (
        <div style={{
          margin: '24px',
          padding: '12px',
          borderRadius: 6,
          ...(optionsError
            ? { background: '#fff2f0', border: '1px solid #ffccc7', color: '#a8071a' }
            : { textAlign: 'center' as const }),
        }}>
          <Text style={optionsError ? { color: '#a8071a' } : undefined}>
            {optionsError ? 'アシストテキストの取得に失敗しました。' : 'アシストテキストがありません。'}
          </Text>
        </div>
      ) : (
        <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}>
          {errorBlock}
          {selectSection}
          <Divider style={{ margin: '12px 0', flexShrink: 0 }} />
          {contentSection}
          {buttonsSection}
        </div>
      )
    ) : (
      <>
        {selectSection}
        <Divider style={{ margin: '12px 0' }} />
        {errorBlock}
        {contentSection}
        {buttonsSection}
      </>
    );

  return (
    <>
      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: MODAL_Z_OVERLAY_MASK }} />
      )}
      <Modal
        title="アシスト入力"
        open={open}
        onCancel={onClose}
        footer={null}
        destroyOnClose
        width={width}
        wrapClassName={wrapClassName}
        bodyStyle={bodyStyle}
        mask={false}
        maskClosable={false}
        zIndex={MODAL_Z_OVERLAY_MASK}
        wrapStyle={WRAP_STYLE}
        transitionName=""
        modalRender={(modal) => (
          <div
            style={
              layout === 'flex'
                ? { height: '100%', maxHeight: '90dvh', display: 'flex', flexDirection: 'column' }
                : undefined
            }
            onTouchMove={(e) => e.stopPropagation()}
          >
            {modal}
          </div>
        )}
      >
        {body}
      </Modal>
    </>
  );
};

export default AssistInputModal;
